import { getHandleFieldChange, getHasError } from "../../utils/formHelpers";
import {
    Button,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    colors,
    FormControlLabel,
    Grid,
    Modal,
    TextField,
    Theme,
    Typography
} from "@material-ui/core";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { T } from "react-polyglot-hooks";
import { makeStyles } from "@material-ui/styles";
import validate from "validate.js";
import sendEmailToGroup, {
    SendEmailToGroupInput
} from "./sendEmailToGroup.api";
import { useSnackbar } from "notistack";
import { LoadingOverlay } from "../LoadingOverlay";
import getEmails from "../../views/EmailManagement/getEmails.api";
import getAllGroups from "../../views/Groups/GroupMemberDetails/getAllGroups.api";
import { OPERATOR } from "../../graphql/common.datatypes";

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        outline: "none",
        boxShadow: theme.shadows[20],
        width: 700,
        maxHeight: "90%",
        overflowY: "auto",
        maxWidth: "95%"
    },
    container: {
        marginTop: theme.spacing(3)
    },
    actions: {
        justifyContent: "flex-end"
    },
    deleteButton: {
        color: theme.palette.white,
        backgroundColor: colors.red[600],
        "&:hover": {
            backgroundColor: colors.red[900]
        }
    }
}));

const SendEmailToGroup = (props: {
    className?: string;
    onClose: () => void;
    onSendClick?: () => void;
    open: boolean;
    heading: string;
    message: string;
    tourId?: number;
    groupId?: number;
    billingPartyId?: number;
    fromEmail?: string;
    softCancelled?: boolean;
    individual?: boolean;
}) => {
    const {
        open,
        onClose,
        onSendClick,
        heading,
        message,
        className,
        tourId,
        groupId,
        billingPartyId,
        fromEmail,
        softCancelled,
        individual = false,
        ...rest
    } = props;
    const classes = useStyles();
    const [postSendEmailToGroup, sendEmailToGroupData] = sendEmailToGroup();
    const { enqueueSnackbar } = useSnackbar();
    const emailData = getEmails();
    const emails = (emailData && emailData.data && emailData.data.emails) || [];

    const allGroupsData = getAllGroups(
        {
            AND: [
                {
                    column: "tour_resource_id",
                    operator: OPERATOR.EQ,
                    value: tourId
                }
            ]
        },
        [{ column: "name", order: "ASC" }]
    );

    const [selectedCheckboxes, setSelectedCheckboxes] = useState<number[]>([]);

    //state variable to keep track of the selected checkboxes.
    const handleSelectAllChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (event.target.checked) {
            if (allGroupsData.data && allGroupsData.data.allGroups) {
                const allGroupValues = allGroupsData.data.allGroups.map(
                    group => group.id
                );
                setSelectedCheckboxes(allGroupValues);
            }
        } else {
            setSelectedCheckboxes([]);
        }
    };

    const handleCheckboxChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const checkboxValue = Number(event.target.value);
        if (event.target.checked) {
            setSelectedCheckboxes([...selectedCheckboxes, checkboxValue]);
        } else {
            setSelectedCheckboxes(
                selectedCheckboxes.filter(value => value !== checkboxValue)
            );
        }
    };

    //1. Set form and error state
    const [formState, setFormState] = useState({
        subject: "",
        email: fromEmail ? fromEmail : "",
        message: "",
        emailSoftCancelledConfirmation: false
    });
    const [errors, setErrors] = useState({
        isValid: false,
        errors: {
            subject: false,
            email: false,
            message: false
        },
        // I don't like this line but I see it is what they are doing in other components
        // when tracking errors outside of the above errors object
        softCancelledConfirmation: false ? "" : undefined
    });

    //2. Setup helper functions for form
    const handleFieldChange = getHandleFieldChange(setFormState);
    const hasError = getHasError(errors);

    //3. setup validation and error hook
    const validateSchema: any = {
        subject: {
            presence: { allowEmpty: false, message: "is required" }
        },
        email: {
            presence: { allowEmpty: false, message: "is required" },
            email: true,
            length: {
                maximum: 64
            }
        },
        message: {
            presence: { allowEmpty: false, message: "is required" }
        }
    };

    useEffect(() => {
        const newErrors = validate(formState, validateSchema);
        const confirmationRequired = softCancelled
            ? !formState.emailSoftCancelledConfirmation
            : false;

        setErrors(state => ({
            ...state,
            isValid: !newErrors && !confirmationRequired,
            errors: newErrors || {},
            softCancelledConfirmation: confirmationRequired
                ? "Please confirm you would like to email this soft cancelled party"
                : undefined
        }));
    }, [formState]);

    if (!open) {
        return null;
    }

    const handleSubmit = () => {
        if (individual) {
            // must have at least one recipient
            if (selectedCheckboxes.length === 0) {
                enqueueSnackbar("Please select at least one recipient", {
                    variant: "error"
                });
                return;
            }
        }

        //sanitise / translate to graphql objects
        const sanitisedData: SendEmailToGroupInput = {
            data: {
                tour_id: tourId,
                group_id: groupId,
                billing_party_id: billingPartyId,
                email: formState.email,
                subject: formState.subject,
                message: formState.message,
                softCancelledConfirmation:
                formState.emailSoftCancelledConfirmation,
                recipients: selectedCheckboxes
            }
        };

        //send to server.
        postSendEmailToGroup({ variables: sanitisedData })
            .then(() => {
                enqueueSnackbar("Message Sent", { variant: "success" });
            })
            .catch(error => {
                console.error("Error:", error); // Log the error to the console
                enqueueSnackbar("Message Failed!", { variant: "error" });
            });
    };

    return (
        <Modal onClose={onClose} open={open}>
            <Card {...rest} className={clsx(classes.root, className)}>
                <form>
                    <LoadingOverlay
                        loading={
                            sendEmailToGroupData.loading ||
                            allGroupsData.loading
                        }
                    >
                        <CardContent>
                            <Typography gutterBottom variant="h3">
                                {heading}
                            </Typography>
                            <Typography gutterBottom variant="body1">
                                {message}
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Subject"
                                        name="subject"
                                        {...hasError("subject")}
                                        onChange={handleFieldChange}
                                        value={formState.subject}
                                        variant="outlined"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="From Email"
                                        name="email"
                                        {...hasError("email")}
                                        onChange={handleFieldChange}
                                        select
                                        // eslint-disable-next-line react/jsx-sort-props
                                        SelectProps={{ native: true }}
                                        InputLabelProps={{ shrink: true }}
                                        value={String(formState.email)}
                                        variant="outlined"
                                    >
                                        <option value="">
                                            please select...
                                        </option>
                                        {emails.map((email, i) => (
                                            <option key={i} value={email.email}>
                                                {email.email}
                                            </option>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        rowsMax={3}
                                        label="Message"
                                        name="message"
                                        {...hasError("message")}
                                        onChange={handleFieldChange}
                                        value={formState.message}
                                        variant="outlined"
                                    />
                                </Grid>

                                {individual &&
                                    allGroupsData.data &&
                                    allGroupsData.data.allGroups && (
                                        <Grid
                                            item
                                            xs={12}
                                            style={{
                                                maxHeight: "300px",
                                                overflowY: "auto",
                                                width: "100%"
                                            }}
                                        >
                                            <Checkbox
                                                checked={
                                                    selectedCheckboxes.length ===
                                                    allGroupsData.data.allGroups
                                                        .length
                                                }
                                                onChange={handleSelectAllChange}
                                            />
                                            <Typography
                                                component="span"
                                                gutterBottom
                                                variant="overline"
                                            >
                                                Select All
                                            </Typography>

                                            {allGroupsData &&
                                                allGroupsData.data &&
                                                allGroupsData.data.allGroups &&
                                                allGroupsData.data.allGroups.map(
                                                    group => {
                                                        return (
                                                            <div key={group.id}>
                                                                <Checkbox
                                                                    key={
                                                                        group.id
                                                                    }
                                                                    value={group.id.toString()} // Convert number to string if necessary
                                                                    checked={selectedCheckboxes.includes(
                                                                        group.id
                                                                    )}
                                                                    onChange={
                                                                        handleCheckboxChange
                                                                    }
                                                                />
                                                                <Typography
                                                                    component="span"
                                                                    gutterBottom
                                                                    variant="overline"
                                                                >
                                                                    {group.name}
                                                                </Typography>
                                                            </div>
                                                        );
                                                    }
                                                )}
                                        </Grid>
                                    )}

                                <Grid item xs={12}>
                                    {softCancelled && (
                                        <FormControlLabel
                                            label="This party has been soft cancelled. Check the box if you'd like to proceed with emailing the party."
                                            control={
                                                <Checkbox
                                                    checked={
                                                        formState.emailSoftCancelledConfirmation
                                                    }
                                                    onChange={handleFieldChange}
                                                    name="emailSoftCancelledConfirmation"
                                                />
                                            }
                                        />
                                    )}
                                    {errors.softCancelledConfirmation && (
                                        <Typography
                                            variant="body1"
                                            color={"error"}
                                        >
                                            {errors.softCancelledConfirmation}
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                        <CardActions className={classes.actions}>
                            <Button onClick={onClose} variant="contained">
                                <T phrase="Cancel" />
                            </Button>
                            <Button
                                className={classes.deleteButton}
                                disabled={!errors.isValid}
                                onClick={() => {
                                    handleSubmit();
                                    onClose();
                                    if (onSendClick) onSendClick();
                                }}
                                variant="contained"
                            >
                                <T phrase="Send" />
                            </Button>
                        </CardActions>
                    </LoadingOverlay>
                </form>
            </Card>
        </Modal>
    );
};

export default SendEmailToGroup;
